import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { pathname } = useLocation();
  const dropdownRef = useRef(null);

  // Luk dropdown og mobilmenu ved ruteskift
  useEffect(() => {
    setIsOpen(false);
    setDropdownOpen(false);
  }, [pathname]);

  // Scroll-lytter til at ændre baggrunden når headeren bliver sticky
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Luk dropdown når man klikker udenfor
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => {
    if (path === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(path);
  };

  const navItems = [
    { label: 'Forside', path: '/' },
    { label: 'Om os', path: '/om-os' },
    {
      label: 'Ydelser',
      path: '/ydelser',
      dropdown: [
        { label: 'Erhvervs Vinduespolering', path: '/ydelser/erhvervs-vinduespolering' },
        { label: 'Privat Vinduespolering', path: '/ydelser/privat-vinduespolering' },
        { label: 'Tagrende rens', path: '/ydelser/tagrende-rens' },
      ],
    },
    { label: 'Galleri', path: '/galleri' },
    { label: 'Kontakt', path: '/kontakt' },
  ];

  return (
    <header className={`sticky top-0 z-50 w-full border-b border-slate-100 transition-all duration-200 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-md'}`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group">
          <img 
            src="https://assets.auxodev.dk/assets/e40be0c5-fb9a-4c6f-b41e-69a05bb6d2d7/303407055-562303575688039-5873020734054747914-n.png" 
            alt="Nygaards Vinduespolering" 
            className="h-24 sm:h-28 lg:h-36 w-auto object-contain transition-transform group-hover:scale-105"
          />
        </Link>

        {/* Desktop Navigation (Vises kun ved 1200px og derover) */}
        <nav className="max-[1200px]:hidden flex items-center gap-8">
          {navItems.map((item) => {
            if (item.dropdown) {
              return (
                <div
                  key={item.label}
                  className="relative"
                  ref={dropdownRef}
                  onMouseEnter={() => setDropdownOpen(true)}
                  onMouseLeave={() => setDropdownOpen(false)}
                >
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className={`flex items-center gap-1.5 font-display text-sm font-semibold transition-colors hover:text-brand-blue ${
                      isActive(item.path) ? 'text-brand-blue' : 'text-slate-600'
                    }`}
                  >
                    {item.label}
                    <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {dropdownOpen && (
                    <div className="absolute left-0 mt-2 w-64 rounded-xl border border-slate-100 bg-white p-2 shadow-xl animate-in fade-in slide-in-from-top-2 duration-200">
                      {/* Invisible bridge to prevent mouse leave */}
                      <div className="absolute -top-2 left-0 right-0 h-2" />
                      <Link
                        to="/ydelser"
                        className={`block rounded-lg px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-400 hover:bg-slate-50`}
                      >
                        Oversigt over ydelser
                      </Link>
                      <hr className="my-1 border-slate-100" />
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`block rounded-lg px-4 py-2.5 text-sm font-medium transition-colors hover:bg-brand-light-blue hover:text-brand-blue ${
                            pathname === subItem.path ? 'bg-brand-light-blue text-brand-blue font-semibold' : 'text-slate-700'
                          }`}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`font-display text-sm font-semibold transition-colors hover:text-brand-blue ${
                  isActive(item.path) ? 'text-brand-blue relative after:absolute after:bottom-[-22px] after:left-0 after:h-[2px] after:w-full after:bg-brand-blue' : 'text-slate-600'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Desktop CTA (Vises kun ved 1200px og derover) */}
        <div className="max-[1200px]:hidden flex items-center gap-4">
          <a
            href="tel:27501226"
            className="flex items-center gap-2 font-display text-sm font-bold text-slate-700 hover:text-brand-blue transition-colors"
          >
            <i className="fa-solid fa-phone text-brand-blue" />
            <span className="whitespace-nowrap">27 50 12 26</span>
          </a>
          <Link
            to="/kontakt"
            className="rounded-xl bg-brand-blue px-5 py-2.5 font-display text-sm font-bold text-white shadow-md shadow-brand-blue/10 hover:bg-brand-orange transition-all hover:shadow-lg hover:shadow-brand-orange/20"
          >
            Få et tilbud
          </Link>
        </div>

        {/* Mobil Menu Knap (Vises under 1200px) */}
        <div className="min-[1200px]:hidden flex items-center gap-4">
          <a
            href="tel:27501226"
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-slate-700 hover:text-brand-blue transition-colors"
            title="Ring op"
          >
            <i className="fa-solid fa-phone" />
          </a>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-100 text-slate-700 hover:bg-slate-50 transition-colors"
            aria-label="Menu"
          >
            <i className={`fa-solid ${isOpen ? 'fa-xmark text-lg' : 'fa-bars'}`} />
          </button>
        </div>
      </div>

      {/* Mobil Menu (Vises under 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden border-t border-slate-100 bg-white px-4 py-6 shadow-inner animate-in slide-in-from-top duration-300 max-h-[calc(100vh-110px)] overflow-y-auto">
          <nav className="flex flex-col gap-4">
            {navItems.map((item) => {
              if (item.dropdown) {
                return (
                  <div key={item.label} className="flex flex-col gap-2">
                    <Link
                      to={item.path}
                      className={`font-display text-base font-bold ${
                        isActive(item.path) ? 'text-brand-blue' : 'text-slate-800'
                      }`}
                    >
                      {item.label}
                    </Link>
                    <div className="ml-4 flex flex-col gap-3 border-l-2 border-slate-100 pl-4">
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`font-display text-sm font-medium ${
                            pathname === subItem.path ? 'text-brand-blue font-semibold' : 'text-slate-600'
                          }`}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`font-display text-base font-bold ${
                    isActive(item.path) ? 'text-brand-blue' : 'text-slate-800'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <hr className="my-2 border-slate-100" />
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1 text-sm text-slate-500">
                <span className="font-semibold text-brand-orange">Nygaards Vinduespolering</span>
                <span className="whitespace-nowrap">Tlf: 27 50 12 26</span>
                <span className="break-all">Email: nygaardspolering@gmail.com</span>
              </div>
              <Link
                to="/kontakt"
                className="w-full rounded-xl bg-brand-blue py-3 text-center font-display text-sm font-bold text-white shadow-md hover:bg-brand-orange transition-colors"
              >
                Få et tilbud
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
